import React, { useState, useEffect } from 'react';
import './AggregationControl.css';
import HelpTooltip from '../HelpTooltip/HelpTooltip';
import { apiGet } from '../../services_1/apiClient';
import useSharedForecastFilters from '../../hooks/useSharedForecastFilters';
import { useAuth } from '../../context/AuthContext';
import { applyRoleLocks, getRoleLocks } from '../../utils_1/roleLocks';

const ROLE_LEVEL_CONFIG = {
  demand_planner: {
    defaultLevel: 'brand_day',
    levels: ['national_day', 'brand_day', 'brand_location_day', 'brand_channel_day', 'sku_day'],
  },
  brand_manager: {
    defaultLevel: 'brand_day',
    levels: ['national_day', 'brand_day', 'sku_day'],
  },
  sales_manager: {
    defaultLevel: 'brand_channel_day',
    levels: ['national_day', 'brand_channel_day'],
  },
  sc_analyst: {
    defaultLevel: 'sku_day',
    levels: ['brand_day', 'sku_day', 'sku_location_day', 'sku_channel_day'],
  },
  executive: {
    defaultLevel: 'national_day',
    levels: ['national_day', 'brand_day', 'brand_location_day', 'brand_channel_day', 'sku_day'],
  },
};

const AggregationControl = ({ onAggregate, onDrillDown, loading, dataSummary }) => {
  const { user } = useAuth();
  const role = user?.role || 'demand_planner';
  const roleLocks = getRoleLocks(role);

  // Determine UI lock state for filters
  const brandLocked = Boolean(roleLocks.lockedBrand);
  const channelLocked = Boolean(roleLocks.lockedChannel);
  
  const [params, setParams] = useSharedForecastFilters([
    'aggregation_filters',
    'daily_forecast_filters',
    'promotional_analysis_filters',
    'scenario_panel_filters',
  ]);

  const roleConfig = ROLE_LEVEL_CONFIG[role] || ROLE_LEVEL_CONFIG.demand_planner;
  const allowedLevels = roleConfig.levels;
  const defaultLevel = roleConfig.defaultLevel;

  useEffect(() => {
    setParams(prev => {
      const next = applyRoleLocks({
        ...prev,
        level: prev.level || defaultLevel,
        forecast_days: prev.forecast_days || 28,
        customer: prev.customer || '',
      }, role);

      const unchanged = Object.keys(next).every(key => next[key] === prev[key]);
      return unchanged ? prev : next;
    });
  }, [role, setParams, defaultLevel]);

  const [dimensionValues, setDimensionValues] = useState({
    brands: [],
    locations: [],
    channels: [],
    customers: []
  });
  const displayBrand = brandLocked ? roleLocks.lockedBrand : params.brand;
  const displayChannel = channelLocked ? roleLocks.lockedChannel : params.channel;
  const brandOptions = brandLocked && roleLocks.lockedBrand && !dimensionValues.brands.includes(roleLocks.lockedBrand)
    ? [roleLocks.lockedBrand, ...dimensionValues.brands]
    : dimensionValues.brands;
  const channelOptions = channelLocked && roleLocks.lockedChannel && !dimensionValues.channels.includes(roleLocks.lockedChannel)
    ? [roleLocks.lockedChannel, ...dimensionValues.channels]
    : dimensionValues.channels;

  const allLevels = [
    { value: 'national_day', label: 'National Level (All Data)' },
    { value: 'brand_day', label: 'Brand Level' },
    { value: 'brand_location_day', label: 'Brand-Location Level' },
    { value: 'brand_channel_day', label: 'Brand-Channel Level' },
    { value: 'sku_day', label: 'SKU Level (Most Detailed)' },
    { value: 'sku_location_day', label: 'SKU-Location Level' },
    { value: 'sku_channel_day', label: 'SKU-Channel Level' }
  ];

  const aggregationLevels = allowedLevels
    .map(levelValue => allLevels.find(lvl => lvl.value === levelValue))
    .filter(Boolean);

  const safeLevel = allowedLevels.includes(params.level) ? params.level : defaultLevel;

  const [currentLevel, setCurrentLevel] = useState(safeLevel);
  const [drillOptions, setDrillOptions] = useState({ drill_down: [], drill_up: [] });
  const quickAggregateLevels = aggregationLevels;
  const quickLevelLabels = {
    national_day: 'National',
    brand_day: 'Brand',
    brand_location_day: 'Brand-Location',
    brand_channel_day: 'Brand-Channel',
    sku_day: 'SKU',
    sku_location_day: 'SKU-Location',
    sku_channel_day: 'SKU-Channel'
  };

  useEffect(() => {
    setCurrentLevel(safeLevel);
    if (params.level !== safeLevel) {
      setParams(prev => ({ ...prev, level: safeLevel }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeLevel]);

  // Load dimension values on mount
  useEffect(() => {
    const loadDimensionValues = async () => {
      try {
        const [brands, locations, channels, customers] = await Promise.all([
          apiGet('/api/dimension-values/brand?limit=100').catch(() => ({ values: [] })),
          apiGet('/api/dimension-values/location?limit=100').catch(() => ({ values: [] })),
          apiGet('/api/dimension-values/channel?limit=100').catch(() => ({ values: [] })),
          apiGet('/api/dimension-values/customer?limit=100').catch(() => ({ values: [] }))
        ]);

        const bVals = brands.values || [];
        const lVals = locations.values || [];
        const cVals = channels.values || [];
        const custVals = customers.values || [];

        setDimensionValues({
          brands: bVals,
          locations: lVals,
          channels: cVals,
          customers: custVals
        });

        // Validate and clean loaded filters against the actual dataset options
        setParams(prev => {
          let updated = { ...prev };
          let changed = false;

          if (prev.brand && !bVals.includes(prev.brand)) {
            updated.brand = '';
            changed = true;
          }
          if (prev.location && !lVals.includes(prev.location)) {
            updated.location = '';
            changed = true;
          }
          if (prev.channel && !cVals.includes(prev.channel)) {
            updated.channel = '';
            changed = true;
          }
          if (prev.customer && !custVals.includes(prev.customer)) {
            updated.customer = '';
            changed = true;
          }

          return changed ? updated : prev;
        });
      } catch (error) {
        console.error('Error loading dimension values:', error);
      }
    };
    loadDimensionValues();
  }, [setParams]);

  // Load drill options when level changes
  useEffect(() => {
    const loadDrillOptions = async () => {
      try {
        const options = await apiGet(`/api/drill-options/${currentLevel}`);
        const allowedSet = new Set(allowedLevels);
        setDrillOptions({
          ...options,
          drill_down: (options.drill_down || []).filter(level => allowedSet.has(level)),
          drill_up: (options.drill_up || []).filter(level => allowedSet.has(level)),
        });
      } catch (error) {
        setDrillOptions({ drill_down: [], drill_up: [] });
      }
    };
    loadDrillOptions();
  }, [currentLevel, allowedLevels]);

  const handleChange = (param, value) => {
    setParams(prev => ({ ...prev, [param]: value }));
    if (param === 'level') {
      setCurrentLevel(value);
    }
  };

  const handleAggregate = () => {
    // Clean up params - only send fields that have values and are relevant
    const selectedLevel = allowedLevels.includes(params.level) ? params.level : defaultLevel;
    const cleanParams = {
      level: selectedLevel,
      forecast_days: params.forecast_days,
      include_promotional: true,
      include_base_demand: true
    };

    // Only add filter params if they have values
    if (params.brand) cleanParams.brand = params.brand;
    if (params.location) cleanParams.location = params.location;
    if (params.channel) cleanParams.channel = params.channel;
    if (params.customer) cleanParams.customer = params.customer;

    console.log('Sending aggregation request:', cleanParams);
    onAggregate(cleanParams);
  };

  const handleDrill = (direction, targetLevel) => {
    const currentFilters = {};

    // Only include filters that have actual values
    if (params.brand) currentFilters.brand = params.brand;
    if (params.location) currentFilters.location = params.location;
    if (params.channel) currentFilters.channel = params.channel;
    if (params.customer) currentFilters.customer = params.customer;

    const drillRequest = {
      from_level: currentLevel,
      to_level: targetLevel,
      direction: direction,
      current_filters: currentFilters,
      target_dimension: '',
      forecast_days: params.forecast_days
    };

    console.log('Sending drill request:', drillRequest);
    onDrillDown(drillRequest);
    setCurrentLevel(targetLevel);
    setParams(prev => ({ ...prev, level: targetLevel }));
  };

  return (
    <div className="control-panel card">
      <div className="panel-header">
        <h3 className="panel-title">Multi-Level Aggregation</h3>
        <div className="level-indicator">
          Current Level: <span className="current-level">{currentLevel.replace('_', ' ').toUpperCase()}</span>
        </div>
      </div>

      <div className="tab-content">
        {/* Aggregation Level Selection */}
        <div className="form-section">
          <h4 className="section-title">Aggregation Level</h4>
          <div className="form-grid grid-2">
            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Target Level <HelpTooltip content="Select the hierarchical level to aggregate the forecast data (e.g., National, Brand, SKU)." /></label>
              <select
                value={currentLevel}
                onChange={(e) => handleChange('level', e.target.value)}
                className="form-input"
                disabled={aggregationLevels.length <= 1}
              >
                {aggregationLevels.map(level => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Forecast Days <HelpTooltip content="The number of days to forecast into the future." /></label>
              <input
                type="number"
                value={params.forecast_days}
                onChange={(e) => handleChange('forecast_days', parseInt(e.target.value) || 30)}
                className="form-input"
                min="1"
                max="90"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4 className="section-title">Filters</h4>
          <div className="form-grid grid-4">
            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Brand {dimensionValues.brands.length > 0 && `(${dimensionValues.brands.length} available)`} <HelpTooltip content="Filter the aggregation to a specific brand." /></label>
              <select
                value={displayBrand}
                onChange={(e) => handleChange('brand', e.target.value)}
                className="form-input"
                disabled={brandLocked}
              >
                <option value="">All Brands</option>
                {brandOptions.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Location {dimensionValues.locations.length > 0 && `(${dimensionValues.locations.length} available)`} <HelpTooltip content="Filter the aggregation to a specific geographical location." /></label>
              <select
                value={params.location}
                onChange={(e) => handleChange('location', e.target.value)}
                className="form-input"
              >
                <option value="">All Locations</option>
                {dimensionValues.locations.map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Channel {dimensionValues.channels.length > 0 && `(${dimensionValues.channels.length} available)`} <HelpTooltip content="Filter the aggregation to a specific sales channel." /></label>
              <select
                value={displayChannel}
                onChange={(e) => handleChange('channel', e.target.value)}
                className="form-input"
                disabled={channelLocked}
              >
                <option value="">All Channels</option>
                {channelOptions.length === 0 ? (
                  <option disabled>No channels found in dataset</option>
                ) : (
                  channelOptions.map(channel => (
                    <option key={channel} value={channel}>{channel}</option>
                  ))
                )}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Customer {dimensionValues.customers.length > 0 && `(${dimensionValues.customers.length} available)`} <HelpTooltip content="Filter the aggregation to a specific customer." /></label>
              <select
                value={params.customer}
                onChange={(e) => handleChange('customer', e.target.value)}
                className="form-input"
              >
                <option value="">All Customers</option>
                {dimensionValues.customers.map(customer => (
                  <option key={customer} value={customer}>{customer}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="form-actions">
          <button
            type="button"
            onClick={handleAggregate}
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Aggregating...' : 'Generate Aggregated Forecast'}
          </button>

          {drillOptions.drill_up && drillOptions.drill_up.length > 0 && (
            <div className="drill-buttons">
              <span className="drill-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                Roll Up:
                <HelpTooltip content="Aggregates data to a higher level of the dimension hierarchy (e.g., from Brand to National)." />
              </span>
              {drillOptions.drill_up.map(level => (
                <button
                  type="button"
                  key={level}
                  onClick={() => handleDrill('up', level)}
                  className="btn btn-secondary btn-sm"
                  disabled={loading}
                >
                  ↑ {quickLevelLabels[level] || level.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          )}

          {drillOptions.drill_down && drillOptions.drill_down.length > 0 && (
            <div className="drill-buttons">
              <span className="drill-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                Drill Down:
                <HelpTooltip content="Breaks down aggregated data into more specific sub-dimensions (e.g., from Brand to SKU)." />
              </span>
              {drillOptions.drill_down.map(level => (
                <button
                  type="button"
                  key={level}
                  onClick={() => handleDrill('down', level)}
                  className="btn btn-secondary btn-sm"
                  disabled={loading}
                >
                  ↓ {quickLevelLabels[level] || level.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Quick Navigation */}
        <div className="quick-nav">
          <h4 className="section-title">Quick Aggregate</h4>
          <div className="quick-buttons">
            {quickAggregateLevels.map(level => (
              <button
                type="button"
                key={level.value}
                onClick={() => handleChange('level', level.value)}
                className="btn btn-outline"
              >
                {quickLevelLabels[level.value] || level.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AggregationControl;
